import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DeleteOutlined,
  EllipsisOutlined,
  KeyOutlined,
  MailOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Dropdown,
  MenuProps,
  Modal,
  notification,
  Tag,
} from 'antd';
import { ColumnsType } from 'antd/es/table';

import Auth0RoleDetailsContext from '@totem/components/services/remoteAccess/auth0role/details/Auth0RoleDetailsContext';
import StepResultsModal from '@totem/components/services/remoteAccess/auth0users/details/StepResultsModal';
import {
  Auth0UserItem,
  MultiStepResults,
} from '@totem/components/services/remoteAccess/auth0users/details/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { normalizeId } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { SecurityGroupCheckConstraints } from '@totem/types/user';
import {
  AUTH0_USER_ENDPOINT,
  REMOTE_ACCESS_USERS_ENDPOINT,
} from '@totem/utilities/endpoints';

const NEVER_LOGGED_IN = '0001-01-01T00:00:00Z';
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const formatDate = (dateStr?: string): string => {
  if (!dateStr || dateStr === NEVER_LOGGED_IN) {
    return 'Never';
  }
  return new Date(dateStr).toLocaleDateString();
};

const isStale = (lastLogin: string, maxInactivityDays?: number): boolean => {
  if (!maxInactivityDays || !lastLogin || lastLogin === NEVER_LOGGED_IN) {
    return false;
  }
  const lastLoginTime = new Date(lastLogin).getTime();
  if (Number.isNaN(lastLoginTime)) {
    return false;
  }
  const daysSinceLogin = (Date.now() - lastLoginTime) / MS_PER_DAY;
  return daysSinceLogin > maxInactivityDays;
};

type ActionKey = 'send_password_reset' | 'send_user_invite' | 'remove_user';

const Auth0RoleUsersTable = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { orgDetails } = useContext(OrganizationContext);
  const { role, users, usersLoading, onRefresh } = useContext(
    Auth0RoleDetailsContext,
  );
  const [workingUserId, setWorkingUserId] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<MultiStepResults | null>(null);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [resultsTitle, setResultsTitle] = useState<string>('');
  const [resultsMessages, setResultsMessages] = useState<{
    success: string;
    failure: string;
  }>({ success: '', failure: '' });

  const checkConstraints: SecurityGroupCheckConstraints = {
    regionId: normalizeId(role?.hierarchyReference?.region?.id ?? ''),
    buildingId: normalizeId(role?.hierarchyReference?.building?.id ?? ''),
    controlSystemId: normalizeId(
      role?.hierarchyReference?.controlSystem?.id ?? '',
    ),
  };

  const isRemoteAccessAdmin = permissions.remoteAccessAdmin.hasPermissions(
    userProfile,
    checkConstraints,
  );
  const isInternal = permissions.internal.hasPermissions(userProfile);

  const handleActionResult = (
    title: string,
    successMessage: string,
    failureMessage: string,
    result: MultiStepResults,
  ) => {
    onRefresh();

    if (isInternal) {
      setResultsTitle(title);

      setResultsMessages({ success: successMessage, failure: failureMessage });

      setLastResult(result);

      setShowResults(true);

      return;
    }

    if (result?.success) {
      notification.success({
        message: successMessage,
        description: successMessage,
      });
    } else {
      notification.error({
        message: failureMessage,
        description: result?.error ?? failureMessage,
        duration: 0,
      });
    }
  };

  const handleActionError = (failureMessage: string) => {
    notification.error({
      message: failureMessage,
      description: failureMessage,
      duration: 0,
    });
  };

  const sendPasswordReset = (user: Auth0UserItem) => {
    if (workingUserId) {
      return;
    }
    setWorkingUserId(user.item.user_id);

    fetch(
      `${AUTH0_USER_ENDPOINT}/${encodeURIComponent(user.item.email)}/passwordReset`,
      {
        method: 'PUT',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    )
      .then((res) => res.json())
      .then((result: MultiStepResults) =>
        handleActionResult(
          'Send Password Reset Result',
          'Password reset sent',
          'Send password reset failed',
          result,
        ),
      )
      .catch(() => handleActionError('Send password reset failed'))
      .finally(() => setWorkingUserId(null));
  };

  const sendUserInvite = (user: Auth0UserItem) => {
    if (workingUserId) {
      return;
    }
    setWorkingUserId(user.item.user_id);

    fetch(
      `${REMOTE_ACCESS_USERS_ENDPOINT}/${encodeURIComponent(user.item.email)}/invite`,
      {
        method: 'PUT',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    )
      .then((res) => res.json())
      .then((result: MultiStepResults) =>
        handleActionResult(
          'Resend Invite Result',
          'Invite sent',
          'Resend invite failed',
          result,
        ),
      )
      .catch(() => handleActionError('Resend invite failed'))
      .finally(() => setWorkingUserId(null));
  };

  const removeUser = (user: Auth0UserItem) => {
    if (workingUserId || !role) {
      return;
    }
    setWorkingUserId(user.item.user_id);

    fetch(
      `${AUTH0_USER_ENDPOINT}/${encodeURIComponent(user.item.user_id)}/roles/${role.id}`,
      {
        method: 'DELETE',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    )
      .then((res) => res.json())
      .then((result: MultiStepResults) =>
        handleActionResult(
          'Remove User Result',
          'User removed',
          'Remove user failed',
          result,
        ),
      )
      .catch(() => handleActionError('Remove user failed'))
      .finally(() => setWorkingUserId(null));
  };

  const confirmAction = (
    key: ActionKey,
    user: Auth0UserItem,
    title: string,
    okText: string,
    onOk: () => void,
  ) => {
    Modal.confirm({
      title,
      okText,
      okType: key === 'remove_user' ? 'danger' : 'primary',
      cancelText: 'Cancel',
      onOk,
    });
  };

  const onMenuClick = (user: Auth0UserItem, key: ActionKey) => {
    switch (key) {
      case 'send_password_reset':
        confirmAction(
          key,
          user,
          `Send a password reset email to ${user.item.email}?`,
          'Send',
          () => sendPasswordReset(user),
        );
        break;
      case 'send_user_invite':
        confirmAction(
          key,
          user,
          `Resend the remote access invite to ${user.item.email}?`,
          'Resend',
          () => sendUserInvite(user),
        );
        break;
      case 'remove_user':
        confirmAction(
          key,
          user,
          `Remove ${user.item.email} from ${role?.name}?`,
          'Remove',
          () => removeUser(user),
        );
        break;
      default:
        break;
    }
  };

  const getActionMenuItems = (): MenuProps['items'] => [
    {
      key: 'send_password_reset',
      label: 'Send Password Reset',
      icon: <KeyOutlined />,
    },
    {
      key: 'send_user_invite',
      label: 'Resend User Invite',
      icon: <MailOutlined />,
    },
    {
      key: 'remove_user',
      label: 'Remove User',
      icon: <DeleteOutlined />,
      danger: true,
    },
  ];

  const columns: ColumnsType<Auth0UserItem> = [
    {
      title: '',
      key: 'picture',
      width: 50,
      render: (_, record) => (
        <Avatar size="default" shape="circle" src={record.item.picture} />
      ),
    },
    {
      title: 'Email',
      key: 'email',
      render: (_, record) => {
        const fromParam = role ? `?from=role:${role.id}:${role.name}` : '';

        return (
          <Link
            to={`/dashboard/remoteAccess/auth0user/${record.item.email}${fromParam}`}
          >
            {record.item.email}
          </Link>
        );
      },
      sorter: (aRow, bRow) => aRow.item.email.localeCompare(bRow.item.email),
    },
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => record.item.name,
      sorter: (aRow, bRow) => aRow.item.name.localeCompare(bRow.item.name),
    },
    {
      title: 'Login Status',
      key: 'loginStatus',
      render: (_, record) => {
        if (record.item.logins_count === 0) {
          return <Tag color="warning">Never Logged In</Tag>;
        }
        if (
          isStale(
            record.item.last_login,
            orgDetails?.extras?.remoteAccessMaxInactivityDays,
          )
        ) {
          return <Tag color="error">Stale</Tag>;
        }
        return <Tag color="success">Active</Tag>;
      },
      sorter: (aRow, bRow) => aRow.item.logins_count - bRow.item.logins_count,
    },
    {
      title: 'Created',
      key: 'createdAt',
      render: (_, record) => formatDate(record.item.created_at),
      sorter: (aRow, bRow) =>
        aRow.item.created_at.localeCompare(bRow.item.created_at),
    },
    {
      title: 'Last Updated',
      key: 'updatedAt',
      render: (_, record) => formatDate(record.item.updated_at),
      sorter: (aRow, bRow) =>
        aRow.item.updated_at.localeCompare(bRow.item.updated_at),
    },
    {
      title: 'Last Login',
      key: 'lastLogin',
      render: (_, record) => formatDate(record.item.last_login),
      sorter: (aRow, bRow) =>
        aRow.item.last_login.localeCompare(bRow.item.last_login),
    },
    {
      title: 'Last Pwd Reset',
      key: 'lastPasswordReset',
      render: (_, record) => formatDate(record.item.last_password_reset),
      sorter: (aRow, bRow) =>
        aRow.item.last_password_reset.localeCompare(
          bRow.item.last_password_reset,
        ),
    },
    ...(isRemoteAccessAdmin
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: 70,
            render: (_: unknown, record: Auth0UserItem) => (
              <Dropdown
                menu={{
                  items: getActionMenuItems(),
                  onClick: ({ key }) => onMenuClick(record, key as ActionKey),
                }}
                trigger={['click']}
              >
                <Button
                  shape="circle"
                  size="small"
                  icon={<EllipsisOutlined />}
                  loading={workingUserId === record.item.user_id}
                />
              </Dropdown>
            ),
          } as ColumnsType<Auth0UserItem>[number],
        ]
      : []),
  ];

  return (
    <>
      <IBTable<Auth0UserItem>
        showSorterTooltip
        columns={columns}
        dataSource={users ?? []}
        loading={usersLoading}
        rowKey={(record) => record.item.user_id}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
      />
      <StepResultsModal
        visible={showResults}
        title={resultsTitle}
        result={lastResult}
        successMessage={resultsMessages.success}
        failureMessage={resultsMessages.failure}
        onClose={() => setShowResults(false)}
      />
    </>
  );
};

export default Auth0RoleUsersTable;
